import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import DocumentTitle from 'react-document-title';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

// Components
import ManageAffordableWaitlistModals from './ManageAffordableWaitlistModals';
import {
  Table,
  TableHeader,
} from '@fortress-technology-solutions/fortress-component-library/Organisms_Fortress';
import {
  Banner,
  BadgeStatus,
} from '@fortress-technology-solutions/fortress-component-library/Molecules';
import {
  AlertInfoIcon,
  CheckIcon,
} from '@fortress-technology-solutions/fortress-component-library/Icons';
import { Switch } from '@fortress-technology-solutions/fortress-component-library/Atoms';
import ManageAffordableWaitlistButtons from './ManageAffordableWaitlistButtons';
import TableLayout from '../../components/TableLayout';

// Hooks
import useSelectedProperty from '../../hooks/useSelectedProperty';
import useManageAffordableWaitlist from './hooks';
import useBaseTableProps from '../../hooks/useBaseTableProps';

// Others
import { AffordableWaitlist } from './types';
import messages from './messages';
import { useAsyncPropertyDetails } from '../ViewProperty/hooks';
import useIntl from '../../hooks/useIntl';
import { promptToaster } from '../App/actions';

/**
 * The central component for the Manage Affordable Waitlist page.
 */
const ManageAffordableWaitlist = ({ actions: { promptToaster } }) => {
  const [isIncludeAll, setIsIncludeAll] = useState(false);
  const { id: propertyId, organizationId } = useSelectedProperty();
  const baseTableProps = useBaseTableProps();
  const intl = useIntl();

  const [propertyDetails] = useAsyncPropertyDetails(
    organizationId,
    propertyId,
    intl,
  );

  const affordableWaitlist =
    propertyDetails?.setup?.affordableWaitlist ?? false;

  const affordableWaitlistStatus =
    propertyDetails?.setup?.affordableWaitlistStatus;

  const {
    modalProps,
    // Handle Sort/Filter/Search
    handleSortChange,
    handleFilterChange,
    handleFilterTypeChange,
    handleSearchSubmit,
    handleDateSubmit,
    filterState,
    filterTypeState,
    searchState,
    dateState,
    // Columns
    handleColumnChange,
    allColumnsHidden,
    columnOptions,
    selectedColumns,
    // Indicators
    isLoading,
    count,
    totalCount,
    name,
    order,
    orderBy,
    // Data
    filteredHeaders,
    refetchAffordableWaitlists,
    rows,
    onCSVButtonClick,
    onPDFButtonClick,
    refetchAffordableWaitlistPropertyPreferences,
    defaultValuesConfigurePreferences,
  } = useManageAffordableWaitlist({
    organizationId,
    propertyId,
    filter: {
      includeAll: isIncludeAll,
    },
    isAffordableWaitlistStatusClosed: affordableWaitlistStatus === 'CLOSED',
  });

  useEffect(() => {
    refetchAffordableWaitlists();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isIncludeAll]);

  const affordableWaitlistItems: Array<AffordableWaitlist> = rows;

  const disableButtons = isLoading || !affordableWaitlist;

  const isAffordableWaitlistStatusOpen = affordableWaitlistStatus === 'OPEN';

  return (
    <DocumentTitle
      data-testid={'manage-affordable-waitlist'}
      title={intl.formatMessage(messages.title)}
    >
      <>
        <TableLayout
          name={name}
          containerElementsHeight={40 + 56 + 8 + 28 + 50}
        >
          {(tableHeight) => (
            <>
              {!isAffordableWaitlistStatusOpen && !isLoading && (
                <Banner
                  withClose
                  icon={<AlertInfoIcon />}
                  text={intl.formatMessage(messages.closedWarning)}
                  color="lightGrey"
                  hasBorder={false}
                  BoxProps={{
                    sx: {
                      marginBottom: '16px',
                    },
                  }}
                />
              )}
              <TableHeader
                title={
                  <span style={{ display: 'flex', alignItems: 'center' }}>
                    <FormattedMessage {...messages.title} />
                    {affordableWaitlist && !isLoading && (
                      <>
                        {affordableWaitlistStatus === 'OPEN' && (
                          <BadgeStatus
                            variant="filled"
                            size="large"
                            status="open"
                            marginLeft="8px"
                          >
                            {intl.formatMessage(messages.open)}
                          </BadgeStatus>
                        )}
                        {affordableWaitlistStatus === 'CLOSED' && (
                          <BadgeStatus
                            variant="filled"
                            size="large"
                            status="closed"
                            marginLeft="8px"
                          >
                            {intl.formatMessage(messages.closed)}
                          </BadgeStatus>
                        )}
                      </>
                    )}
                  </span>
                }
                count={count}
                totalCount={totalCount}
                actions={
                  <ManageAffordableWaitlistButtons
                    organizationId={organizationId}
                    propertyId={propertyId}
                    disabled={disableButtons}
                    openModalConfigurePreferences={(e) => {
                      modalProps.handleOpen({
                        configurePreferences: {
                          ...defaultValuesConfigurePreferences,
                        },
                        modalType: 'configurePreferences',
                      });
                    }}
                    promptToaster={promptToaster}
                  />
                }
              />
              <Switch
                checked={isIncludeAll}
                onChange={() => setIsIncludeAll((prevState) => !prevState)}
                label={<FormattedMessage {...messages.includeAll} />}
                icon={<CheckIcon />}
                checkedIcon={<CheckIcon />}
                id="include-all"
                disabled={isLoading}
              />
              <Table
                {...baseTableProps}
                // Handle Sort/Filter/Search
                handleSortChange={handleSortChange}
                handleFilterChange={handleFilterChange}
                handleFilterTypeChange={handleFilterTypeChange}
                handleSearchSubmit={handleSearchSubmit}
                handleDateSubmit={handleDateSubmit}
                filterState={filterState}
                filterTypeState={filterTypeState}
                searchState={searchState}
                dateState={dateState}
                // Columns
                handleColumnChange={handleColumnChange}
                allColumnsHidden={allColumnsHidden}
                columnOptions={columnOptions}
                selectedColumns={selectedColumns}
                // Indicators
                isLoading={isLoading}
                count={count}
                totalCount={totalCount}
                name={name}
                order={order}
                orderBy={orderBy}
                // Data
                headers={filteredHeaders}
                rows={affordableWaitlistItems}
                onCSVButtonClick={count > 0 ? onCSVButtonClick : undefined}
                onPDFButtonClick={count > 0 ? onPDFButtonClick : undefined}
                // Others
                height={tableHeight}
              />
              <ManageAffordableWaitlistModals
                organizationId={organizationId}
                propertyId={propertyId}
                refetchAffordableWaitlists={refetchAffordableWaitlists}
                refetchAffordableWaitlistPropertyPreferences={
                  refetchAffordableWaitlistPropertyPreferences
                }
                intl={intl}
                {...modalProps}
              />
            </>
          )}
        </TableLayout>
      </>
    </DocumentTitle>
  );
};

export function mapDispatchToProps(dispatch: any): Object {
  const actions = bindActionCreators(
    {
      promptToaster,
    },
    dispatch,
  );
  return { actions };
}

export default connect(null, mapDispatchToProps)(ManageAffordableWaitlist);
